<template>
  <v-dialog
    :value="dialog"
    persistent
    :retain-focus="false"
    transition="fade-transition"
    fullscreen
    class="p-contract-edit-dialog"
    @keydown.esc="escape(closeDialog)"
  >
    <v-toolbar
      color="primaryBgr"
      dark
    >
      <p-btn-icon type="close" btn-class="mr-4" tabindex="3" @click="escape(closeDialog)"></p-btn-icon>
      <v-toolbar-title>{{ $t('schedule.editSchedule') }}</v-toolbar-title>
    </v-toolbar>
    <v-card>
      <v-form ref="form" @submit.stop.prevent>
        <v-container>
          <v-row class="mb-4">
            <v-col cols="4" sm="12" md="6" lg="4" xl="3" class="text-left">
              <v-text-field
                type="text"
                v-model.trim="editedSchedule.name"
                :label="$t('schedule.edit.title')"
                :rules="rules.title"
                prepend-icon="mdi-card-text-outline"
                tabindex="1"
                clearable
                autofocus
              ></v-text-field>
            </v-col>
            <v-col cols="2">
              <v-btn
                color="primary"
                class="mt-4 hidden-sm-and-down"
                :disabled="!isModelChanged()"
                tabindex="2"
                @click="submit"
              >
                {{ $t('save') }}
              </v-btn>
            </v-col>
          </v-row>
          <ejs-documenteditorcontainer
            v-if="ejsReady"
            ref="editor"
            :enableToolbar="true"
            :serviceUrl="editorServiceUrl"
            :documentName="editedSchedule.title"
            :toolbarItems="editorToolbarItems"
            height="700px"
            tabindex="1"
            @toolbarClick="onEditorToolbarClick"
            @contentChange="onEditorContentChange"
          ></ejs-documenteditorcontainer>
        </v-container>
      </v-form>
    </v-card>
  </v-dialog>
</template>


<script>
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

import { DocumentEditorContainerPlugin, Toolbar } from '@syncfusion/ej2-vue-documenteditor'

import PBtnIcon from '@/components/PBtnIcon'

import documentEditorMixin from '@/mixins/documentEditor.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'

Vue.use(DocumentEditorContainerPlugin)


export default {
  provide: {
    DocumentEditorContainer: [Toolbar]
  },


  components: {
    PBtnIcon
  },


  mixins: [
    documentEditorMixin,
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  props: {
    schedule: Object
  },


  data () {
    return {
      editorServiceUrl: process.env.VUE_APP_DOCUMENT_EDITOR_API_URL,
      editedSchedule: {},
      ejsReady: false,
      documentEditor: null,
      preparedSchedule: {}
    }
  },


  computed: {
    ...mapGetters('contact', ['shortContactsForProject']),
    ...mapGetters('project', ['currentProjectId']),

    editorToolbarItems () {
      const docxItem = this.getToolbarItem(this.toolbarItemId.docx)
      const pdfItem = this.getToolbarItem(this.toolbarItemId.pdf)

      return ['New', 'Open', 'Separator', docxItem, pdfItem, 'Separator', 'Undo', 'Redo', 'Separator', 'Image', 'Table', 'Hyperlink', 'TableOfContents', 'Separator', 'Header', 'Footer', 'PageSetup', 'PageNumber', 'Break', 'Separator', 'Find']
    },

    contactsList () {
      return this.shortContactsForProject(this.currentProjectId)
    },

    rulesToApply () {
      const rule = this.rule

      return {
        title: [rule.required()]
      }
    }
  },


  methods: {
    ...mapActions('schedule', ['putSchedule', 'loadScheduleById']),
    ...mapActions('changes', ['removeFromChanges']),

    async onDialogOpen () {
      if (this.schedule) {
        this.removeFromChanges(this.schedule)
      }
      try {
        this.showSnackbarProcessing()
        const schedule = await this.loadScheduleById(this.schedule)
        this.editedSchedule = this.getObjectCopy(schedule)
        this.resetValidation()
        this.hideSnackbar()
        this.prepareEditor()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    async prepareEditor () {
      this.ejsReady = false
      await Vue.nextTick()
      this.ejsReady = true
      await Vue.nextTick()
      this.documentEditor = this.$refs.editor?.ej2Instances?.documentEditor
      this.setEditorContent()
    },

    removeEditor () {
      this.ejsReady = false
    },

    onEditorContentChange () {
      this.updateEditedScheduleBody()
    },

    async onEditorToolbarClick (args) {
      try {
        switch (args.item.id) {
          case this.toolbarItemId.pdf: {
            await this.saveAsPdf(this.$refs.editor, this.editedSchedule.title || 'schedule')
            break
          }
          case this.toolbarItemId.docx: {
            await this.saveAsDocx(this.$refs.editor, this.editedSchedule.title || 'schedule')
            break
          }
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    setEditorContent () {
      if (!this.$refs?.editor) return
      this.documentEditor.open(this.editedSchedule.body)
      this.initWatchedModel('editedSchedule')
    },

    updateEditedScheduleBody () {
      this.editedSchedule.body = this.$refs.editor.ej2Instances.documentEditor.serialize()
    },

    prepareScheduleForPut () {
      this.preparedSchedule = {}
      this.preparedSchedule.id = this.editedSchedule.id
      this.preparedSchedule.projectId = this.editedSchedule.projectId
      this.preparedSchedule.title = this.editedSchedule.name
      this.preparedSchedule.body = this.editedSchedule.body
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        this.prepareScheduleForPut()
        await this.putSchedule(this.preparedSchedule)
        this.showSnackbarSuccess(this.$t('schedule.scheduleUpdated'))
        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.removeEditor()
      }
    }
  }
}
</script>
